import { format, formatDistanceToNowStrict } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { formatInTimeZone } from "date-fns-tz";

import { Address } from "@/typings/address";

export const emptyDisplay = "--";

export const extractName = (name?: string) => {
  const trimmedName = `${name}`.trim();
  const [firstName, ...lastName] = trimmedName.split(" ");
  return {
    firstName: firstName.trim(),
    lastName: lastName.join(" ").trim()
  };
};

export const capitalize = (value) => {
  if (!value) return "";
  const capped = value.toString();
  return capped.charAt(0).toUpperCase() + capped.slice(1);
};

// See: https://stackoverflow.com/questions/196972/convert-string-to-title-case-with-javascript
export function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export const tableFieldDisplay = (val) => {
  const valWithNoUnderscore = val.replaceAll("_", " ");
  return toTitleCase(valWithNoUnderscore);
};

export const tableValueDisplay = (val) => {
  if (val === null || val === undefined) return emptyDisplay;
  if (!isNaN(new Date(val).getTime())) return formatDate(new Date(val));
  return val ? val : emptyDisplay;
};

/**
 * Formats dates in a given timezone (optional)
 *
 * @param {Date|String} userDate Date to be formatted
 * @param {object} options Date format options, supports { timezone: string; format: string; }
 * @returns {string}
 */
const DEFAULT_DATE_FORMAT = "MM/dd/yyyy";
export const formatDate = (userDate, options = {} as any) => {
  if (!userDate) return emptyDisplay;
  try {
    const date = new Date(userDate);
    const dateFormat = options.format || DEFAULT_DATE_FORMAT;
    return options.timezone
      ? formatInTimeZone(date, options.timezone, dateFormat, { locale: enUS })
      : format(date, dateFormat, { locale: enUS });
  } catch (e) {
    console.warn(e);
    return "Invalid date";
  }
};

export const formatAddressLines = (address: Address) => {
  if (!address) return "";

  const { street, unit, city, state, zip_code } = address;
  const streetWithUnit = unit ? `${street}, ${unit}` : street;
  const addressString = `${streetWithUnit}\n${city}, ${state} ${zip_code}`;

  if (addressString === "null\nnull, null null") {
    return "Missing Address!";
  }

  return addressString;
};

// Converts cents to dollars as a string
export const dollars = (cents, options = {}) => {
  if (!cents && cents !== 0) {
    return "--";
  }
  const fixed = (cents / 100).toFixed(2);
  try {
    return Number(parseFloat(fixed)).toLocaleString("en-us", {
      style: "currency",
      currency: "USD",
      ...options
    });
  } catch (e) {
    return "$0.00";
  }
};

export const fullAddress = (address) => {
  const { street, unit, city, state, zip_code } = address;
  return unit
    ? `${street}, ${unit}, ${city}, ${state} ${zip_code}`
    : `${street} ${city}, ${state} ${zip_code}`;
};
export const fromNow = (dateVal, includeSuffix = false) => {
  if (!dateVal) return "--";
  try {
    return formatDistanceToNowStrict(new Date(dateVal), {
      roundingMethod: "floor",
      addSuffix: includeSuffix
    });
  } catch (e) {
    return "Invalid date";
  }
};

export const NO_DATE_ON_RECORD = "--";
export const LESS_THAN_A_MONTH = "Less than a month";

// Mirrors EquipmentComponent#age_with_words on the backend: month arithmetic by
// calendar year/month (no day-of-month), with explicit phrases for missing and
// sub-1-month equipment so the UI never has to render a blank cell.
export const yearsAndMonthsFromNow = (
  dateVal: Date | string | null | undefined
): string => {
  if (!dateVal) return NO_DATE_ON_RECORD;

  const ref = new Date(dateVal);
  if (isNaN(ref.getTime())) return NO_DATE_ON_RECORD;

  const now = new Date();
  const totalMonths =
    now.getUTCFullYear() * 12 +
    now.getUTCMonth() -
    (ref.getUTCFullYear() * 12 + ref.getUTCMonth());

  if (totalMonths < 1) return LESS_THAN_A_MONTH;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr`);
  if (months > 0) parts.push(`${months} mo`);

  return parts.join(" ");
};
